import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostIwocapaySellerOnboardingAnswers } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { Form, Formik, useField } from 'formik';
import * as Yup from 'yup';

import styles from './YourBusiness.module.css';
import {
  useGetSellerOnboardingAnswers,
  useGetSellerOnboardingRequirements,
} from '../../../api/lending/lapiHooks';
import { MultipleChoiceDropdown } from '../../../Buyer/Signup/components/MultipleChoiceDropdown/MultipleChoiceDropdown';
import { RadioButton } from '../../../Buyer/Signup/components/RadioButton/RadioButton';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { Input } from '../../../components/Input/Input';
import { useGetCustomerEmail } from '../../../hooks/useGetCustomerEmail';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { postYourBusinessToHubspot } from '../hubspotApi';

export type TYourBusinessFormValues = {
  annual_turnover: string;
  invoices_per_month: string;
  reason_for_use: string;
  accept_b2b_payments?: string;
  url_online_store: string;
  ecomm_platform_use: string[];
  accountancy_platform_use: string[];
};

export const YourBusiness = () => {
  const { stateKey } = useStateKey();
  const { fetchSellerOnboardingRequirements: fetchRequirements } =
    useGetSellerOnboardingRequirements();
  const { sellerOnboardingAnswers: answers } = useGetSellerOnboardingAnswers();
  const { isMobile } = useIsMobile();
  const email = useGetCustomerEmail();

  const formValues: TYourBusinessFormValues = {
    annual_turnover: answers?.annual_turnover || '',
    invoices_per_month: answers?.invoices_per_month || '',
    reason_for_use: answers?.reason_for_use || '',
    accept_b2b_payments: `${answers?.accept_b2b_payments}` || undefined,
    url_online_store: answers?.url_online_store || '',
    ecomm_platform_use: answers?.ecomm_platform_use || [],
    accountancy_platform_use: answers?.accountancy_platform_use || [],
  };

  const handleSubmit = async (value: TYourBusinessFormValues) => {
    // ratio returns a string, we need to provide a boolean
    // @ts-expect-error Normalising the data before sending out
    value.accept_b2b_payments =
      value.accept_b2b_payments === 'true' ? true : false;

    if (!stateKey) {
      throw Error('No statekey found');
    }

    if (!email) {
      throw Error('No email found');
    }

    tracking.ampli.businessDetailsSubmitted();

    await fetchPostIwocapaySellerOnboardingAnswers({
      stateKey,
      // @ts-expect-error
      body: { data: value },
    });
    // @ts-expect-error Result of expect-error above.
    // TS doesn't infer type correctly
    await postYourBusinessToHubspot({ email, ...value });
    void fetchRequirements();
  };

  return (
    <div>
      <p className={styles.yourBusinessText}>
        This helps us understand your business and decide which features we
        build next.
      </p>
      <Formik
        enableReinitialize={true}
        onSubmit={handleSubmit}
        initialValues={formValues}
        validationSchema={validation}
      >
        <Form className={styles.form}>
          <Dropdown
            name="annual_turnover"
            labelText="What was your last 12 months turnover from B2B sales?"
            placeholder="Select an option"
          >
            <option value="no_sales_to_b2b_customers">
              No sales to B2B customers
            </option>
            <option value="£1k-£100k">£1 - £100k</option>
            <option value="£101k-£500k">£101k - £500k</option>
            <option value="£500k-£1m">£501k - £1m</option>
            <option value="£1m-£10m">£1m - £10m</option>
            <option value="£10m+">£10m+</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </Dropdown>
          <Dropdown
            name="invoices_per_month"
            labelText="How many B2B payments do you take per month?"
            placeholder="Select an option"
          >
            <option value="no_b2b_payments">No B2B payments</option>
            <option value="1-10">1 - 10</option>
            <option value="11-20">11 - 20</option>
            <option value="21-30">21 - 30</option>
            <option value="30+">30+</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </Dropdown>
          <RadioButton
            labelText="Do you accept B2B payments on your website?"
            name="accept_b2b_payments"
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ]}
            compact={isMobile ? false : true}
            className={styles.radioButton}
          />
          <URLDetailBlock />
          <MultipleChoiceDropdown
            name="accountancy_platform_use"
            labelText="What accounting software do you use?"
            subtitleText="You can select multiple"
            placeholder="Select option(s)..."
            fieldData={[
              { id: 'xero', title: 'Xero' },
              { id: 'quickbooks', title: 'QuickBooks' },
              { id: 'freeagent', title: 'FreeAgent' },
              { id: 'sage', title: 'Sage' },
              { id: 'zoho', title: 'Zoho' },
              { id: 'netsuite', title: 'Netsuite' },
              { id: 'crunch', title: 'Crunch' },
              { id: 'freshbooks', title: 'Freshbooks' },
              {
                id: 'i_dont_use_an_accountancy_platform',
                title: "I don't use an accountancy platform",
              },
              { id: 'other', title: 'Other' },
              { id: 'prefer_not_to_say', title: 'Prefer not to say' },
            ]}
          />
          <Dropdown
            name="reason_for_use"
            labelText="What is your primary reason for using iwocaPay?"
            placeholder="Select an option"
          >
            <option value="avoid_paying_card_fees">Avoid card fees</option>
            <option value="increase_average_order_value">
              Increase order value
            </option>
            <option value="make_it_easier_for_customers_to_spread_the_cost">
              Help customers spread the cost
            </option>
            <option value="offer_more_competetive_payment_terms">
              Offer competitive payment terms
            </option>
            <option value="improve_cashflow">Improve my cashflow</option>
            <option value="get_paid_faster">Get paid faster</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </Dropdown>
          <Button
            variant="primary"
            type="submit"
            className={styles.submitButton}
          >
            Continue
          </Button>
        </Form>
      </Formik>
    </div>
  );
};

const URLDetailBlock = () => {
  const [field] = useField('accept_b2b_payments');

  return (
    <div className={styles.emphasisBlock}>
      <Input
        label="What is the url of your online store?"
        labelDescriptionText=""
        name="url_online_store"
        className={styles.signupInput}
        placeholder="e.g. www.yourwebsite.com"
        disabled={field.value === 'true' ? false : true}
      />
      <MultipleChoiceDropdown
        name="ecomm_platform_use"
        disabled={field.value === 'true' ? false : true}
        labelText="Which ecommerce platform(s) do you use?"
        subtitleText="You can select multiple"
        placeholder="Select option(s)..."
        fieldData={[
          { id: 'magento', title: 'Magento' },
          { id: 'magento_2', title: 'Magento 2' },
          { id: 'woocommerce', title: 'WooCommerce' },
          { id: 'opencart', title: 'OpenCart' },
          { id: 'shopify', title: 'Shopify' },
          { id: 'bigcommerce', title: 'BigCommerce' },
          { id: 'prestashop', title: 'PrestaShop' },
          { id: 'custom_built_site', title: 'Custom built site' },
          {
            id: 'i_sell_on_a_b2b_marketplace',
            title: 'I sell on a B2B marketplace',
          },
          { id: 'other', title: 'Other' },
          { id: 'prefer_not_to_say', title: 'Prefer not to say' },
        ]}
      />
    </div>
  );
};

const validation = Yup.object().shape({
  annual_turnover: Yup.string().required('Your annual turnover is required'),
  invoices_per_month: Yup.string().required(
    'The number of B2B payments taken is required',
  ),
  reason_for_use: Yup.string().required(
    'Your reason for using iwocaPay is required',
  ),
  accept_b2b_payments: Yup.string().oneOf(
    ['true', 'false'],
    'Whether you accept B2B payments is required',
  ),
  url_online_store: Yup.string().when('accept_b2b_payments', {
    is: (value: string) => value === 'true',
    then: (schema) =>
      schema
        .min(1)
        .matches(
          /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
          'Please enter a valid URL e.g. https://iwoca.co.uk/',
        )
        .required('Your store url is required'),
    otherwise: (schema) => schema,
  }),
  ecomm_platform_use: Yup.array().when('accept_b2b_payments', {
    is: (value: string) => value === 'true',
    then: (schema) => schema.min(1, 'Your ecommerce platform use is required'),
    otherwise: (schema) => schema,
  }),
  accountancy_platform_use: Yup.array().min(
    1,
    'Your accounting platform use is required',
  ),
});
